import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Monitor, Smartphone, Tablet } from 'lucide-react';
import { fetchAllRows } from '@/lib/supabaseHelpers';

interface Session {
  device_type: string | null;
  is_completed: boolean | null;
  current_step: number | null;
}

const DEVICE_ICONS: Record<string, any> = {
  mobile: Smartphone,
  tablet: Tablet,
  desktop: Monitor,
};

const LiveDeviceConversion = () => {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [pageviews, setPageviews] = useState<{ device_type: string | null }[]>([]);

  const fetchData = async () => {
    const [sessData, pvData] = await Promise.all([
      fetchAllRows<Session>('quiz_sessions', 'device_type, is_completed, current_step', [
        { column: 'utm_source', operator: 'not.is', value: null },
      ]),
      fetchAllRows<{ device_type: string | null }>('quiz_pageviews', 'device_type', [
        { column: 'utm_source', operator: 'not.is', value: null },
      ]),
    ]);
    setSessions(sessData);
    setPageviews(pvData);
  };

  useEffect(() => {
    fetchData();
    const channel = supabase
      .channel('live-device-conv')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'quiz_sessions' }, () => fetchData())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'quiz_pageviews' }, () => fetchData())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  // Group by device
  const devices = new Map<string, { pageviews: number; started: number; completed: number }>();
  
  pageviews.forEach(pv => {
    const d = pv.device_type || 'onbekend';
    if (!devices.has(d)) devices.set(d, { pageviews: 0, started: 0, completed: 0 });
    devices.get(d)!.pageviews++;
  });

  sessions.forEach(s => {
    const d = s.device_type || 'onbekend';
    if (!devices.has(d)) devices.set(d, { pageviews: 0, started: 0, completed: 0 });
    const entry = devices.get(d)!;
    if ((s.current_step ?? 0) >= 2) entry.started++;
    if (s.is_completed) entry.completed++;
  });

  const deviceData = Array.from(devices.entries())
    .map(([name, data]) => ({ name, ...data }))
    .sort((a, b) => b.pageviews - a.pageviews);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
      <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
        📱 Conversie per apparaat
        <span className="ml-auto flex items-center gap-1.5 text-xs font-medium text-emerald-600">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
          </span>
          LIVE
        </span>
      </h3>
      <div className="space-y-3">
        {deviceData.map(d => {
          const Icon = DEVICE_ICONS[d.name] || Monitor;
          const cvr = d.pageviews > 0 ? Math.round((d.completed / d.pageviews) * 100) : 0;
          const startRate = d.pageviews > 0 ? Math.round((d.started / d.pageviews) * 100) : 0;
          return (
            <div key={d.name} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
              <Icon className="w-5 h-5 text-gray-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-900 capitalize">{d.name}</span>
                  <span className="text-xs text-gray-500">{d.pageviews} pv → {d.completed} voltooid</span>
                </div>
                <div className="flex gap-3 text-xs">
                  <span className="text-blue-600 font-medium">Start: {startRate}%</span>
                  <span className="text-emerald-600 font-medium">CVR: {cvr}%</span>
                </div>
                <div className="h-1.5 bg-gray-200 rounded-full mt-1.5 overflow-hidden">
                  <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${cvr}%` }} />
                </div>
              </div>
            </div>
          );
        })}
        {deviceData.length === 0 && (
          <p className="text-sm text-gray-400">Nog geen data</p>
        )}
      </div>
    </div>
  );
};

export default LiveDeviceConversion;
